
import React, { useEffect, useState, useRef, useCallback } from "react";
import Header from "../Components/Header";

const API = "https://api.welbeing.in";

const parseResponse = async (response) => {
    let data;

    try {
        data = await response.json();
    } catch {
        throw new Error("Invalid response received from the server.");
    }

    if (!response.ok) {
        throw new Error(
            data?.message || `Request failed (${response.status}).`
        );
    }

    return data;
};

const NewArrivals = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [addedProducts, setAddedProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState("");
    const [deletingId, setDeletingId] = useState(null);
    const [adding, setAdding] = useState(false);

    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const addingRef = useRef(false);
    const deletingRef = useRef(null);
    const mountedRef = useRef(false);

    const fetchAllProducts = useCallback(async () => {
        const response = await fetch(
            `${API}/admin/products/all-products/`
        );
        const data = await parseResponse(response);

        if (data.status !== "success" || !Array.isArray(data.data)) {
            throw new Error(data.message || "Failed to fetch products.");
        }

        setAllProducts(data.data);
    }, []);

    const fetchNewArrivals = useCallback(async () => {
        const response = await fetch(`${API}/home/new-arrival/`);
        const data = await parseResponse(response);

        if (data.status !== "success" || !Array.isArray(data.data)) {
            throw new Error(data.message || "Failed to fetch new arrivals.");
        }

        setAddedProducts(data.data);
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const results = await Promise.allSettled([
                    fetchAllProducts(),
                    fetchNewArrivals(),
                ]);

                if (!mountedRef.current) return;

                const failures = results
                    .filter((result) => result.status === "rejected")
                    .map((result) => result.reason);

                if (failures.length > 0) {
                    setError(
                        failures.map((err) => err.message).join(" ")
                    );
                }
            } catch {
                if (mountedRef.current) {
                    setError("Failed to load products.");
                }
            } finally {
                if (mountedRef.current) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            mountedRef.current = false;
        };
    }, [fetchAllProducts, fetchNewArrivals]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener(
                "mousedown",
                handleOutsideClick
            );
        };
    }, []);

    useEffect(() => {
        if (dropdownOpen) {
            searchInputRef.current?.focus();
        }
    }, [dropdownOpen]);

    const filteredProducts = allProducts.filter((product) => {
        const term = searchTerm.trim().toLowerCase();

        return (
            product.item_name?.toLowerCase().includes(term) ||
            String(product.item_code ?? "").toLowerCase().includes(term)
        );
    });

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError(null);
        setSuccessMessage("");

        if (!selectedProduct) {
            setError("Please select a product.");
            return;
        }

        // Prevent duplicate requests from rapid clicks or submissions.
        if (addingRef.current) return;

        addingRef.current = true;
        setAdding(true);

        try {
            const response = await fetch(
                `${API}/home/add-newarrival/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        p_id: selectedProduct.p_id,
                    }),
                }
            );

            const data = await parseResponse(response);

            if (data.status !== "success") {
                throw new Error(
                    data.message || "Failed to add product to New Arrivals."
                );
            }

            const productName = selectedProduct.item_name;

            await fetchNewArrivals();

            if (!mountedRef.current) return;

            setSuccessMessage(
                `"${productName}" added to New Arrivals!`
            );
            setSelectedProduct(null);
            setSearchTerm("");
            setDropdownOpen(false);
        } catch (err) {
            if (mountedRef.current) {
                setError(err.message || "Failed to add product.");
            }
        } finally {
            addingRef.current = false;

            if (mountedRef.current) {
                setAdding(false);
            }
        }
    };

    const handleDelete = async (id, name) => {
        if (deletingRef.current !== null) return;

        deletingRef.current = id;
        setDeletingId(id);
        setError(null);
        setSuccessMessage("");

        try {
            const response = await fetch(
                `${API}/home/delete-newarrival/`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ id }),
                }
            );

            const data = await parseResponse(response);

            if (data.status !== "success") {
                throw new Error(
                    data.message || "Failed to remove product."
                );
            }

            if (!mountedRef.current) return;

            setAddedProducts((previous) =>
                previous.filter((item) => item.id !== id)
            );

            setSuccessMessage(
                `"${name || "Product"}" removed from New Arrivals.`
            );
        } catch (err) {
            if (mountedRef.current) {
                setError(err.message || "Failed to remove product.");
            }
        } finally {
            deletingRef.current = null;

            if (mountedRef.current) {
                setDeletingId(null);
            }
        }
    };

    return (
        <>
            <Header />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="alert alert-info">
                                    Note: The New Arrivals section will be
                                    displayed on the website only when a
                                    minimum of <b>4 products</b> are added.
                                </div>

                                {successMessage && (
                                    <div
                                        className="alert alert-success alert-dismissible fade show"
                                        role="status"
                                    >
                                        {successMessage}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={() =>
                                                setSuccessMessage("")
                                            }
                                        />
                                    </div>
                                )}

                                {error && (
                                    <div
                                        className="alert alert-danger alert-dismissible fade show"
                                        role="alert"
                                    >
                                        {error}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            aria-label="Close"
                                            onClick={() => setError(null)}
                                        />
                                    </div>
                                )}

                                <div className="mb-3">
                                    <h5
                                        className="mb-0 fw-bold d-inline-block px-3 py-2 rounded"
                                        style={{
                                            fontSize: "30px",
                                            background:
                                                "linear-gradient(135deg, #0d6efd15, #0d6efd08)",
                                            borderLeft:
                                                "5px solid #0d6efd",
                                            color: "#0d6efd",
                                            letterSpacing: "0.3px",
                                        }}
                                    >
                                        Add New Arrival Product
                                    </h5>
                                </div>

                                <form onSubmit={handleSubmit}>
                                    <div className="form-body">
                                        <div className="row align-items-end">
                                            <div className="col-md-6">
                                                <div
                                                    className="mb-3"
                                                    ref={dropdownRef}
                                                    style={{
                                                        position: "relative",
                                                    }}
                                                >
                                                    <label
                                                        htmlFor="product-search"
                                                        className="form-label"
                                                    >
                                                        Select Product
                                                    </label>

                                                    <button
                                                        type="button"
                                                        className="form-control d-flex justify-content-between align-items-center text-start"
                                                        aria-expanded={
                                                            dropdownOpen
                                                        }
                                                        aria-haspopup="listbox"
                                                        onClick={() => {
                                                            setDropdownOpen(
                                                                (open) => !open
                                                            );
                                                        }}
                                                    >
                                                        <span
                                                            className={
                                                                selectedProduct
                                                                    ? ""
                                                                    : "text-muted"
                                                            }
                                                        >
                                                            {selectedProduct
                                                                ? selectedProduct.item_name
                                                                : "Search & select a product…"}
                                                        </span>

                                                        <i
                                                            className={`ti ti-chevron-${
                                                                dropdownOpen
                                                                    ? "up"
                                                                    : "down"
                                                            } ms-2`}
                                                        />
                                                    </button>

                                                    {dropdownOpen && (
                                                        <div
                                                            className="card shadow"
                                                            style={{
                                                                position:
                                                                    "absolute",
                                                                top: "calc(100% + 4px)",
                                                                left: 0,
                                                                right: 0,
                                                                zIndex: 999,
                                                                border: "1px solid #dee2e6",
                                                                borderRadius:
                                                                    "8px",
                                                                overflow:
                                                                    "hidden",
                                                            }}
                                                        >
                                                            <div className="card-body p-0">
                                                                <div className="p-2 border-bottom">
                                                                    <input
                                                                        ref={
                                                                            searchInputRef
                                                                        }
                                                                        id="product-search"
                                                                        type="search"
                                                                        className="form-control form-control-sm"
                                                                        placeholder="Filter by product name or code…"
                                                                        value={
                                                                            searchTerm
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            setSearchTerm(
                                                                                event
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                        aria-label="Search products"
                                                                    />
                                                                </div>

                                                                <ul
                                                                    className="list-unstyled mb-0"
                                                                    role="listbox"
                                                                    aria-label="Products"
                                                                    style={{
                                                                        maxHeight:
                                                                            "200px",
                                                                        overflowY:
                                                                            "auto",
                                                                    }}
                                                                >
                                                                    {filteredProducts.length ===
                                                                    0 ? (
                                                                        <li className="text-center text-muted py-3">
                                                                            No
                                                                            products
                                                                            found
                                                                        </li>
                                                                    ) : (
                                                                        filteredProducts.map(
                                                                            (
                                                                                product
                                                                            ) => (
                                                                                <li
                                                                                    key={
                                                                                        product.p_id
                                                                                    }
                                                                                    role="option"
                                                                                    aria-selected={
                                                                                        selectedProduct?.p_id ===
                                                                                        product.p_id
                                                                                    }
                                                                                >
                                                                                    <button
                                                                                        type="button"
                                                                                        className={`w-100 text-start border-0 px-3 py-2 ${
                                                                                            selectedProduct?.p_id ===
                                                                                            product.p_id
                                                                                                ? "bg-light fw-semibold"
                                                                                                : "bg-white"
                                                                                        }`}
                                                                                        style={{
                                                                                            fontSize:
                                                                                                "0.9rem",
                                                                                        }}
                                                                                        onClick={() => {
                                                                                            setSelectedProduct(
                                                                                                product
                                                                                            );
                                                                                            setSearchTerm(
                                                                                                ""
                                                                                            );
                                                                                            setDropdownOpen(
                                                                                                false
                                                                                            );
                                                                                        }}
                                                                                    >
                                                                                        {selectedProduct?.p_id ===
                                                                                            product.p_id && (
                                                                                            <i className="ti ti-check me-2 text-primary" />
                                                                                        )}

                                                                                        {
                                                                                            product.item_name
                                                                                        }

                                                                                        {product.item_code && (
                                                                                            <small className="d-block text-muted">
                                                                                                {
                                                                                                    product.item_code
                                                                                                }
                                                                                            </small>
                                                                                        )}
                                                                                    </button>
                                                                                </li>
                                                                            )
                                                                        )
                                                                    )}
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="mb-3">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-light-primary text-primary font-medium w-100"
                                                        disabled={
                                                            !selectedProduct ||
                                                            adding
                                                        }
                                                    >
                                                        {adding ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm me-2"
                                                                    role="status"
                                                                />
                                                                Adding…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ti ti-plus me-1" />
                                                                Add
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>

                                <hr />

                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                                    <h5 className="mb-0">
                                        New Arrivals List
                                    </h5>

                                    <span className="badge bg-dark fs-6">
                                        {addedProducts.length}{" "}
                                        {addedProducts.length === 1
                                            ? "Product"
                                            : "Products"}
                                    </span>
                                </div>

                                <div
                                    className="table-responsive"
                                    style={{ overflowX: "auto" }}
                                >
                                    <table className="table border table-striped table-bordered align-middle">
                                        <thead>
                                            <tr>
                                                <th>S.No</th>
                                                <th>Product Name</th>
                                                <th>Item Code</th>
                                                <th className="text-center">
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td
                                                        colSpan="4"
                                                        className="text-center py-4"
                                                    >
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                        />
                                                        Loading products…
                                                    </td>
                                                </tr>
                                            ) : addedProducts.length ===
                                              0 ? (
                                                <tr>
                                                    <td
                                                        colSpan="4"
                                                        className="text-center fw-bold"
                                                    >
                                                        No new arrivals added
                                                        yet.
                                                    </td>
                                                </tr>
                                            ) : (
                                                addedProducts.map(
                                                    (product, index) => (
                                                        <tr key={product.id}>
                                                            <td>
                                                                {index + 1}
                                                            </td>
                                                            <td>
                                                                <h6 className="mb-0">
                                                                    {
                                                                        product.item_name
                                                                    }
                                                                </h6>
                                                            </td>
                                                            <td>
                                                                {product.item_code ||
                                                                    "—"}
                                                            </td>
                                                            <td className="text-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    aria-label={`Remove ${
                                                                        product.item_name ||
                                                                        "product"
                                                                    }`}
                                                                    onClick={() =>
                                                                        handleDelete(
                                                                            product.id,
                                                                            product.item_name
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        deletingId !==
                                                                            null ||
                                                                        adding
                                                                    }
                                                                >
                                                                    {deletingId ===
                                                                    product.id ? (
                                                                        <>
                                                                            <span
                                                                                className="spinner-border spinner-border-sm me-1"
                                                                                role="status"
                                                                            />
                                                                            Removing…
                                                                        </>
                                                                    ) : (
                                                                        <i className="ti ti-trash" />
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    )
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NewArrivals;
